// src/utils/packBadge.js
// Migrated verbatim from prototype/between-the-lines-v2.html lines 6330-6464.
// 14 named exports: pack-cover / pack-badge visual helpers used by
// DashboardPage's pack-card rendering JSX (Sprint 2b-2 onwards).
//
// Skipped (admin-tools only, out of Sprint 2b-2 scope):
//   - PACK_THEME_ORDER (prototype line 6342)
//   - LABEL_POSITION_OPTIONS (prototype lines 6357-6363)
//   - PACK_BADGE_OPTIONS (prototype lines 6389-6395)

import { LEGACY_PACK_ID } from '../config/packTemplates.js';
import { getPackType } from './packHelpers.js';

// === Cover palette (library-2c) ===
// The dark-academia set (forest/burgundy/oxford/...) is gone. Every theme is now
// DERIVED from a single cover hex, so the 10 named swatches and a per-pack custom
// colour go down exactly the same code path -- adding a colour is one table row, and
// a colour Danielle picks in the admin behaves identically to a built-in.
export const COVER_INK_LIGHT = '#FFFFFF';
export const COVER_INK_DARK = '#1B1A17';
export const DEFAULT_THEME_KEY = 'violet';

// The 10 approved swatches. Order is the order the admin shows them in.
export const PACK_COVER_SWATCHES = [
    { key: 'violet', name: 'Violet', hex: '#6C4CF1' },
    { key: 'coral',  name: 'Coral',  hex: '#F26B5E' },
    { key: 'teal',   name: 'Teal',   hex: '#1FA39A' },
    { key: 'amber',  name: 'Amber',  hex: '#F2A93B' },
    { key: 'sky',    name: 'Sky',    hex: '#3B8BEA' },
    { key: 'mint',   name: 'Mint',   hex: '#3DD6A2' },
    { key: 'rose',   name: 'Rose',   hex: '#E85D9B' },
    { key: 'lime',   name: 'Lime',   hex: '#C6E24A' },
    { key: 'indigo', name: 'Indigo', hex: '#4B3FBF' },
    { key: 'slate',  name: 'Slate',  hex: '#4A5568' }
];

// Old theme keys stored on live packs keep working -- and keep their identity where
// there is an honest match (burgundy -> coral, forest -> teal). No data migration:
// a pack nobody re-saves resolves through this map on every render. `leather` has no
// distinct new equivalent, so it lands on slate alongside obsidian.
export const LEGACY_THEME_ALIASES = {
    amethyst: 'violet',
    burgundy: 'coral',
    forest:   'teal',
    copper:   'amber',
    oxford:   'sky',
    sage:     'mint',
    crimson:  'rose',
    midnight: 'indigo',
    obsidian: 'slate',
    leather:  'slate'
};

// A #rrggbb string (with or without the #, any case) -> [r,g,b], else null.
function hexToRgb(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex == null ? '' : hex).trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// Same shape as the backend allowlist in functions/src/schema/packSchema.js: a strict
// 6-digit hex and nothing else. Used by the admin picker so a bad value is refused in
// the form rather than silently dropped on save.
export function normalizeCoverColor(hex) {
    // Type-check FIRST, exactly as the CF does. String(['#f26b5e']) is '#f26b5e', so
    // coercing first would make the client accept shapes the backend stores as null.
    if (typeof hex !== 'string') return null;
    const s = hex.trim();
    return /^#[0-9a-fA-F]{6}$/.test(s) ? s.toLowerCase() : null;
}

// WCAG relative luminance / contrast ratio. Exported because the admin preview has to
// show the user what their custom colour will actually score before they save it.
export function relativeLuminance(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;
    const lin = (c) => { const s = c / 255; return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
    return 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
}

export function contrastRatio(a, b) {
    const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}

// Pick the title colour for a cover: whichever of white / near-black actually scores
// better on it. A ratio comparison rather than a luminance threshold, so the answer is
// exact at the crossover instead of near it.
export function pickInk(hex) {
    if (!hexToRgb(hex)) return COVER_INK_LIGHT;
    return contrastRatio(COVER_INK_LIGHT, hex) >= contrastRatio(COVER_INK_DARK, hex)
        ? COVER_INK_LIGHT : COVER_INK_DARK;
}

// A cover hex -> the full theme object the card/spine/reader chrome consume. Pure.
// Everything readable is derived from the ink, so it inherits the ink's contrast:
// no per-colour hand-tuning, and a custom colour is as safe as a built-in.
export function deriveCoverTheme(hex, name) {
    const cover = normalizeCoverColor(hex) || PACK_COVER_SWATCHES[0].hex.toLowerCase();
    const ink = pickInk(cover);
    const rgb = hexToRgb(ink).join(',');
    const spine = darkenHex(cover, 0.12);
    return {
        name: name || 'Custom',
        cover,
        spine,
        primary: ink,
        // lesson-3a housekeeping (carried from the 2c handback): the cover sub-label
        // was ink @80%, which missed AA on five of the ten swatches -- 3.84:1 on sky.
        // No alpha below 1.0 fixes that, so the sub-label now uses the FULL ink and
        // earns its hierarchy from a lighter weight and smaller size instead.
        secondary: ink,
        line: 'rgba(' + rgb + ',0.3)',
        text: ink,
        scrollThumb: spine
    };
}

// === PACK_THEMES ===
// Keyed the same way as before (consumers index by theme key), but every entry is now
// just deriveCoverTheme() of its swatch.
export const PACK_THEMES = PACK_COVER_SWATCHES.reduce((acc, s) => {
    acc[s.key] = deriveCoverTheme(s.hex, s.name);
    return acc;
}, {});

// Resolve a stored theme key: a current key wins, a legacy key maps through the alias
// table, anything unknown falls back to the default rather than rendering blank.
export function resolveThemeKey(key) {
    const k = String(key == null ? '' : key).trim();
    if (PACK_THEMES[k]) return k;
    if (LEGACY_THEME_ALIASES[k]) return LEGACY_THEME_ALIASES[k];
    return DEFAULT_THEME_KEY;
}

// === getPackTheme ===
// Precedence: a valid per-pack coverColor > the pack's theme key (alias-resolved) >
// the default. coverColor wins so the custom picker is always the last word.
export const getPackTheme = (pack) => {
    const custom = normalizeCoverColor(pack?.coverColor);
    if (custom) return deriveCoverTheme(custom);
    return PACK_THEMES[resolveThemeKey(pack?.theme)];
};

// Cover typography  controls font size, position, and visibility of cover labels
// === LABEL_SIZE_OPTIONS (prototype 6346-6351) ===
export const LABEL_SIZE_OPTIONS = [
    { value: 'small',  label: 'Small (24px)',  px: 24 },
    { value: 'medium', label: 'Medium (32px)', px: 32 },
    { value: 'large',  label: 'Large (40px)',  px: 40 },
    { value: 'xlarge', label: 'X-Large (48px)', px: 48 }
];

// === SUBLABEL_SIZE_OPTIONS (prototype 6352-6356) ===
export const SUBLABEL_SIZE_OPTIONS = [
    { value: 'small',  label: 'Small (12px)',  px: 12 },
    { value: 'medium', label: 'Medium (14px)', px: 14 },
    { value: 'large',  label: 'Large (16px)',  px: 16 }
];

// === DEFAULT_COVER_TYPOGRAPHY (prototype 6364-6372) ===
export const DEFAULT_COVER_TYPOGRAPHY = {
    labelSize: 'medium',
    labelPosition: 'center',
    labelVisible: true,
    subLabelSize: 'medium',
    subLabelVisible: true,
    paperCodeVisible: true,
    dividerVisible: true
};

// === getPackTypography (prototype 6373) ===
export const getPackTypography = (pack) => ({ ...DEFAULT_COVER_TYPOGRAPHY, ...(pack?.typography || {}) });

// === labelSizePx (prototype 6374) ===
export const labelSizePx = (key) => (LABEL_SIZE_OPTIONS.find(o => o.value === key) || LABEL_SIZE_OPTIONS[1]).px;

// === subLabelSizePx (prototype 6375) ===
export const subLabelSizePx = (key) => (SUBLABEL_SIZE_OPTIONS.find(o => o.value === key) || SUBLABEL_SIZE_OPTIONS[1]).px;

// === fitTitleFontPx (A2: pack-title auto-fit) ===
// Content-responsive title sizing so long pack titles (e.g.
// "MACBETH-GRADE_9_LITERATURE_STUDY_PACK") don't overflow the cover/card. Keep the
// chosen `maxPx` for short titles; scale it DOWN proportionally once the title's
// longest unbreakable token -- or half its total length for multi-word titles --
// exceeds `refChars`, floored at `minPx`. Pure + deterministic (no DOM measure) so it
// is node-testable; pair it with an overflowWrap:'anywhere' style on the node so a
// shrunken long token also wraps tidily instead of overflowing. NEVER scales UP.
//   short title ("MACBETH")            -> maxPx (unchanged, stays large)
//   long token  ("MACBETH-GRADE_9...") -> round(maxPx * refChars / metric), >= minPx
export function fitTitleFontPx(text, maxPx, { minPx = 13, refChars = 14 } = {}) {
    const s = String(text == null ? '' : text).trim();
    if (!s) return maxPx;
    const longestToken = s.split(/\s+/).reduce((m, w) => Math.max(m, w.length), 0);
    const metric = Math.max(longestToken, s.length / 2);
    if (metric <= refChars) return maxPx;
    return Math.max(minPx, Math.round((maxPx * refChars) / metric));
}

// Convert a position key to positioning CSS for an absolute-positioned label block inside the cover
// === positionStyles (prototype 6377-6387) ===
export const positionStyles = (pos) => {
    const inset = 16;
    switch (pos) {
        case 'top-left':      return { top: inset, left: inset + 8, right: inset, textAlign: 'left' };
        case 'top-center':    return { top: inset, left: inset, right: inset, textAlign: 'center' };
        case 'bottom-left':   return { bottom: inset, left: inset + 8, right: inset, textAlign: 'left' };
        case 'bottom-center': return { bottom: inset, left: inset, right: inset, textAlign: 'center' };
        case 'center':
        default:              return { top: '50%', left: inset, right: inset, transform: 'translateY(-50%)', textAlign: 'center' };
    }
};

// === BADGE_CLASS (prototype 6396-6401) ===
export const BADGE_CLASS = {
    'free':        'bg-burgundy-accent text-burgundy-pageDark',
    'coming-soon': 'border border-burgundy-accent/60 text-burgundy-accent bg-burgundy-bg',
    'new':         'bg-burgundy-accent text-burgundy-pageDark',
    'updated':     'bg-blue-900 text-white'
};

// Count free preview items for a pack. Treat the RESULT as a "has free content"
// signal (> 0), NOT an exact per-type count -- see the set-text caveat below.
//   Set Text        -> state.texts (legacy pack) / state.packContent[id].texts where free===true
//   Skills Workshop -> state.packContent[id].units where free===true
//   Literature Study-> state.packContent[id].items where free===true (ALL section kinds)
// CAVEAT (root cause of the over-counting "11 FREE" badge): only the LEGACY set-text
// pack populates state.texts. Non-legacy set-text packs render from the freeItems
// subcollection (usePackItems, in the reader) and never fill state.packContent[id].texts,
// so this falls through to `pack.freeItems` = freeItemIds.length (usePacksBridge) -- the
// count of ALL free items (texts + flashcards + quizzes...), not free texts only. A true
// texts-only count isn't available here (per-item types aren't in app state), so the cover
// badge is intentionally NON-NUMERIC ("Free view") rather than showing a wrong number
// (see getPackBadge). This value remains correct as a >0 "has free content" gate.
// === getPackFreeCount (prototype 6407-6431) ===
export const getPackFreeCount = (state, pack) => {
    if (!pack || !state) return Number(pack?.freeItems || 0);
    const type = getPackType(pack);
    if (type === 'set-text') {
        const texts = pack.id === LEGACY_PACK_ID ? (state.texts || []) : (state.packContent?.[pack.id]?.texts || []);
        const cnt = texts.filter(t => t?.free).length;
        return cnt > 0 ? cnt : Number(pack.freeItems || 0);
    }
    if (type === 'skills-workshop') {
        const units = state.packContent?.[pack.id]?.units || [];
        const cnt = units.filter(u => u?.free).length;
        return cnt > 0 ? cnt : Number(pack.freeItems || 0);
    }
    if (type === 'literature-study') {
        const pc = state.packContent?.[pack.id] || {};
        // Count free items in the items map (covers acts, characters, themes,
        // symbols, sample-essays, quote-bank, matrix  any sidebar entry
        // with free: true). Lock state is independent: free=true is what
        // the admin uses to flag preview content.
        const items = pc.items || {};
        const cnt = Object.values(items).filter(it => it?.free).length;
        return cnt > 0 ? cnt : Number(pack.freeItems || 0);
    }
    return Number(pack.freeItems || 0);
};

// Label the "Preview free X ->" link by pack type
// === getPackFreeLabel (prototype 6433-6439) ===
export const getPackFreeLabel = (pack) => {
    const type = getPackType(pack);
    if (type === 'set-text') return 'texts';
    if (type === 'skills-workshop') return 'units';
    if (type === 'literature-study') return 'sections';
    return 'items';
};

// === getPackBadge (prototype 6440-6454) ===
export const getPackBadge = (pack, state) => {
    const manual = pack?.badge;
    if (manual === 'None') return null;
    if (manual === 'Free')        return { label: 'Free',        kind: 'free' };
    if (manual === 'Coming Soon') return { label: 'Coming Soon', kind: 'coming-soon' };
    if (manual === 'New')         return { label: 'New',         kind: 'new' };
    if (manual === 'Updated')     return { label: 'Updated',     kind: 'updated' };
    // Auto fallback when no manual override: show a NON-NUMERIC "Free view" affordance
    // when the pack has any free content (free > 0). Deliberately not "N Free": the count
    // is an all-free-items total for non-legacy set-text packs, not free-texts-only (see
    // getPackFreeCount caveat), so a number would mislead. The cover renders it uppercase
    // ("FREE VIEW"). No free content -> no badge.
    if (state) {
        const free = getPackFreeCount(state, pack);
        if (free > 0) return { label: 'Free view', kind: 'free' };
    }
    if (pack?.status === 'coming-soon') return { label: 'Coming Soon', kind: 'coming-soon' };
    return null;
};

// Darken a hex colour by `amount` (0-1). Used to derive spine from cover.
// A hoisted function declaration, not a const arrow: deriveCoverTheme() above calls it
// while the module is still evaluating (PACK_THEMES is built at load), which a const
// would trip over.
// === darkenHex (prototype 6456-6464) ===
export function darkenHex(hex, amount = 0.35) {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex;
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    if (full.length !== 6) return hex;
    const toDark = (n) => Math.max(0, Math.round(parseInt(full.slice(n, n + 2), 16) * (1 - amount)));
    const [r, g, b] = [toDark(0), toDark(2), toDark(4)];
    return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}
